<template>
	<div>
		<div v-if="loading" class="loading-container">
			<i class="fas fa-spinner loading-spinner" />
		</div>

		<SizerErrors v-if="sizerStore.errors" :errors="sizerStore.errors" />

		<slot v-else-if="sizerStore.out" name="results">
			<DefaultSizerResults />
		</slot>

		<slot v-else name="banner">
			<DefaultSizerIntro />
		</slot>

		<form @submit.prevent="handleSubmit" @keydown.enter="preventEnterSubmit">
			<slot name="form-sections" />

			<DefaultSizerSubmit>
				{{submitText}}
			</DefaultSizerSubmit>
		</form>
	</div>
</template>

<script>
import { defineAsyncComponent } from 'vue'
import DefaultSizerIntro from '@/components/DefaultSizerIntro'
import DefaultSizerSubmit from '@/components/DefaultSizerSubmit'
import formData from '@/components/Sizer/form-data-store'
import jsonFetch from '@/utils/json-fetch'
import sizerStore from './sizer-store'

export default {
	name: 'SizerHome',

	components: {
		DefaultSizerIntro,
		DefaultSizerSubmit,
		DefaultSizerResults: defineAsyncComponent(
			() => import('@/components/DefaultSizerResults')
		),
		SizerErrors: defineAsyncComponent(
			() => import('@/components/SizerErrors')
		),
	},

	props: {
		submitPath: {
			type: String,
			default: '/sizer/size/',
		},
		submitText: {
			type: String,
			default: 'Size Your System',
		},
		initFormData: {
			type: Object,
			default: () => ({}),
		},
		onResults: {
			type: Function,
			default: () => null,
		},
		submitMethod: {
			type: Function,
		},
	},

	data: () => ({
		sizerStore,
		loading: false,
	}),

	created () {
		const { initFormData } = this

		Object.keys(formData).forEach(key => {
			delete formData[key]
		})

		Object.keys(sizerStore).forEach(key => {
			delete sizerStore[key]
		})

		Object.entries(initFormData).forEach(([key, value]) => {
			formData[key] = value
		})
	},

	methods: {
		async defaultSubmit () {
			const { submitPath: url, onResults, formatOutData } = this
			this.loading = true
			this.resultsData = null

			const {
				errors,
				out,
				primaryCurveDiv,
				plotSimDiv,
			} = await jsonFetch({ url, body: formData })

			sizerStore.out = out && formatOutData(out)
			sizerStore.primaryCurveDiv = primaryCurveDiv
			sizerStore.plotSimDiv = plotSimDiv
			sizerStore.errors = errors

			// If there's a recircFlow error, set formData.errors to expand Advanced Schematic Options
			if (errors && errors.recircFlow) {
				formData.errors = true
			} else {
				formData.errors = false
			}

			// If there's a percentUseable error, set flag to expand Water Temperature Advanced Options
			if (errors && errors.percentUseable) {
				formData.percentUseableError = true
			} else {
				formData.percentUseableError = false
			}

			formData.TMCap_kW = !formData.sizeAdditionalER && out && out.swingtankkW || formData.TMCap_kW
			this.loading = false

			onResults({ success: !errors })
		},

		handleSubmit() {
			// Validate temperature order before submitting
			const validationErrors = this.validateTemperatures()
			if (Object.keys(validationErrors).length > 0) {
				sizerStore.errors = validationErrors
				sizerStore.out = null
				formData.errors = false // No recircFlow error, don't expand Advanced Schematic Options
				return
			}

			if (this.submitMethod) {
				this.loading = true
				this.submitMethod(); // Use the custom submit function if provided
				this.loading = false
			} else {
				this.defaultSubmit(); // Otherwise, use the default submit logic
			}
		},

		preventEnterSubmit(e) {
			if (e.target.tagName !== 'TEXTAREA') {
				e.preventDefault()
			}
		},

		validateTemperatures() {
			const errors = {}
			const { incomingT, supplyT, storageT } = formData

			// Design cold water temp must be less than supply water temp
			if (Number(incomingT) >= Number(supplyT)) {
				errors['incomingT'] = ['Design Cold Water Temperature must be less than Supply Water Temperature']
			}

			// Supply water temp must be less than hot storage temp
			if (Number(supplyT) >= Number(storageT)) {
				errors['supplyT'] = ['Supply Water Temperature must be less than Hot Storage Temperature']
			}

			return errors
		},

		formatOutData (out) {
			const [x_data, y_data] = [out.x_data, out.y_data].map(data => {
				return data && JSON.parse(data)
			})

			return {
				...out,
				x_data,
				y_data,
			}
		}
	}
}
</script>

<style scoped>
.loading-container {
	z-index: 10;
	position: fixed;
	top: 0;
	left: 0;
	width: 100vw;
	height: 100vh;
	background-color: black;
	opacity: .3;
}

.loading-spinner {
	font-size: 3rem;
	color:  white;
	animation: spin 2s linear infinite;
	position: absolute;
	top: 45%;
	left: 45%;
}

@keyframes spin {
	from { transform: rotate(0deg) }
	to { transform: rotate(360deg) }
}
</style>
